/**
 * Read / instrument the PM UI iframe when Complic runs in open-complic-chrome.sh
 * (--disable-web-security). Falls back gracefully in normal browsers.
 */

export type IframeConsoleEntry = { ts: string; type: string; text: string };
export type IframeNetEntry = {
  ts: string;
  phase?: string;
  method?: string;
  url?: string;
  status?: number;
  ok?: boolean;
};

export type IframeNavLink = { text: string; href: string };

export type IframeUiElement = {
  role: string;
  name: string;
  tag: string;
  href?: string;
  testId?: string;
};

export type IframeProbe = {
  readable: boolean;
  url: string;
  title: string;
  path: string;
  body_text: string;
  nav_links: IframeNavLink[];
  ui_map: IframeUiElement[];
  headings: string[];
  console: IframeConsoleEntry[];
  network: IframeNetEntry[];
  error?: string;
};

const HOOK_FLAG = '__complicIframeHooked';

function nowIso() {
  return new Date().toISOString();
}

function pushCap<T>(arr: T[], item: T, max = 100) {
  arr.push(item);
  if (arr.length > max) arr.splice(0, arr.length - max);
}

/** Install console + fetch hooks inside the iframe window (once). */
export function ensureIframeHooks(
  iframe: HTMLIFrameElement,
  sinks: {
    console: IframeConsoleEntry[];
    network: IframeNetEntry[];
  }
): boolean {
  try {
    const w = iframe.contentWindow as any;
    const doc = iframe.contentDocument;
    if (!w || !doc) return false;
    // Probe access
    void doc.location.href;

    if (w[HOOK_FLAG]) return true;
    w[HOOK_FLAG] = true;

    const wrap = (type: string, fn: (...a: any[]) => void) => {
      return (...args: any[]) => {
        try {
          pushCap(sinks.console, {
            ts: nowIso(),
            type,
            text: args
              .map((a) => {
                try {
                  return typeof a === 'string' ? a : JSON.stringify(a);
                } catch {
                  return String(a);
                }
              })
              .join(' ')
              .slice(0, 2000),
          });
        } catch {
          /* ignore */
        }
        return fn.apply(w.console, args);
      };
    };

    const c = w.console;
    c.log = wrap('log', c.log.bind(c));
    c.info = wrap('info', c.info.bind(c));
    c.warn = wrap('warn', c.warn.bind(c));
    c.error = wrap('error', c.error.bind(c));
    c.debug = wrap('debug', c.debug.bind(c));

    w.addEventListener('error', (ev: ErrorEvent) => {
      pushCap(sinks.console, {
        ts: nowIso(),
        type: 'error',
        text: ev.message || String(ev.error || 'error'),
      });
    });
    w.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      pushCap(sinks.console, {
        ts: nowIso(),
        type: 'error',
        text: `unhandledrejection: ${String(ev.reason)}`.slice(0, 2000),
      });
    });

    if (typeof w.fetch === 'function') {
      const origFetch = w.fetch.bind(w);
      w.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const method = (init?.method || 'GET').toUpperCase();
        const url =
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.href
              : (input as Request).url;
        pushCap(sinks.network, {
          ts: nowIso(),
          phase: 'request',
          method,
          url: String(url),
        });
        try {
          const res = await origFetch(input, init);
          pushCap(sinks.network, {
            ts: nowIso(),
            phase: 'response',
            method,
            url: String(url),
            status: res.status,
            ok: res.ok,
          });
          return res;
        } catch (e) {
          pushCap(sinks.network, {
            ts: nowIso(),
            phase: 'response',
            method,
            url: String(url),
            status: 0,
            ok: false,
          });
          throw e;
        }
      };
    }

    return true;
  } catch (e: any) {
    return false;
  }
}

export function probeIframe(
  iframe: HTMLIFrameElement | null,
  sinks: { console: IframeConsoleEntry[]; network: IframeNetEntry[] }
): IframeProbe {
  if (!iframe) {
    return emptyProbe({ error: 'no iframe' });
  }
  try {
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) {
      return emptyProbe({
        console: [...sinks.console],
        network: [...sinks.network],
        error: 'iframe not ready',
      });
    }
    const href = doc.location.href;
    ensureIframeHooks(iframe, sinks);
    const body = doc.body?.innerText || '';
    return {
      readable: true,
      url: href,
      title: doc.title || '',
      path: doc.location.pathname + doc.location.search + doc.location.hash,
      body_text: body.replace(/\s+/g, ' ').trim().slice(0, 6000),
      nav_links: extractNavLinks(doc),
      ui_map: extractUiMap(doc),
      headings: extractHeadings(doc),
      console: [...sinks.console],
      network: [...sinks.network],
    };
  } catch (e: any) {
    return emptyProbe({
      console: [...sinks.console],
      network: [...sinks.network],
      error: e?.message || 'cross-origin blocked',
    });
  }
}

export type IframeCommand = {
  id: string;
  action: string;
  selector?: string | null;
  text?: string | null;
  url?: string | null;
  key?: string | null;
  value?: string | null;
};

function elLabel(el: HTMLElement): string {
  return (el.innerText || (el as HTMLInputElement).value || el.getAttribute('aria-label') || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function findByText(doc: Document, text: string): HTMLElement | null {
  const needle = text.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!needle) return null;
  const candidates = Array.from(
    doc.querySelectorAll(
      'a, button, [role="button"], [role="menuitem"], [role="link"], label, summary, option, input[type="submit"], input[type="button"]'
    )
  ) as HTMLElement[];
  // Prefer exact label match (avoids wrong menu items)
  for (const el of candidates) {
    if (elLabel(el) === needle) return el;
  }
  for (const el of candidates) {
    const t = elLabel(el);
    if (t.includes(needle) || needle.includes(t)) return el;
  }
  // broader: any clickable with matching text
  const all = Array.from(doc.querySelectorAll('body *')) as HTMLElement[];
  for (const el of all) {
    if (el.children.length > 3) continue;
    const t = (el.innerText || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (t === needle) return el;
  }
  return null;
}

/** Visible nav/menu links so the agent can discover real paths (not invent Czech URLs). */
function extractNavLinks(doc: Document, limit = 80): IframeNavLink[] {
  const out: IframeNavLink[] = [];
  const seen = new Set<string>();
  const anchors = Array.from(doc.querySelectorAll('a[href]')) as HTMLAnchorElement[];
  for (const a of anchors) {
    const href = (a.getAttribute('href') || '').trim();
    if (!href || href.startsWith('javascript:') || href === '#') continue;
    const text = (a.innerText || a.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim();
    if (!text || text.length > 80) continue;
    const key = `${text}|${href}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ text, href });
    if (out.length >= limit) break;
  }
  return out;
}

function roleOf(el: HTMLElement): string {
  const r = (el.getAttribute('role') || '').trim();
  if (r) return r;
  const tag = el.tagName.toLowerCase();
  if (tag === 'a') return 'link';
  if (tag === 'button') return 'button';
  if (tag === 'input') return `input:${(el as HTMLInputElement).type || 'text'}`;
  if (tag === 'select') return 'select';
  if (tag === 'textarea') return 'textbox';
  if (tag === 'h1' || tag === 'h2' || tag === 'h3') return 'heading';
  return tag;
}

/** Interactive / landmark map of the current screen (for orientation). */
function extractUiMap(doc: Document, limit = 120): IframeUiElement[] {
  const out: IframeUiElement[] = [];
  const seen = new Set<string>();
  const nodes = Array.from(
    doc.querySelectorAll(
      'a[href], button, [role="button"], [role="link"], [role="menuitem"], [role="tab"], input, select, textarea, [data-testid], h1, h2, h3'
    )
  ) as HTMLElement[];
  for (const el of nodes) {
    try {
      const style = doc.defaultView?.getComputedStyle?.(el);
      if (style && (style.display === 'none' || style.visibility === 'hidden')) continue;
    } catch {
      /* ignore */
    }
    const name = (
      el.getAttribute('aria-label') ||
      el.innerText ||
      (el as HTMLInputElement).placeholder ||
      (el as HTMLInputElement).value ||
      el.getAttribute('title') ||
      ''
    )
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 100);
    if (!name && el.tagName !== 'INPUT') continue;
    const href = el.tagName === 'A' ? (el as HTMLAnchorElement).getAttribute('href') || undefined : undefined;
    const testId = el.getAttribute('data-testid') || undefined;
    const role = roleOf(el);
    const key = `${role}|${name}|${href || ''}|${testId || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      role,
      name: name || '(unnamed)',
      tag: el.tagName.toLowerCase(),
      href: href || undefined,
      testId,
    });
    if (out.length >= limit) break;
  }
  return out;
}

function extractHeadings(doc: Document, limit = 20): string[] {
  return Array.from(doc.querySelectorAll('h1, h2, h3'))
    .map((el) => (el.textContent || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .slice(0, limit);
}

function emptyProbe(partial: Partial<IframeProbe> & { error?: string }): IframeProbe {
  return {
    readable: false,
    url: '',
    title: '',
    path: '',
    body_text: '',
    nav_links: [],
    ui_map: [],
    headings: [],
    console: [],
    network: [],
    ...partial,
  };
}

function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const desc = Object.getOwnPropertyDescriptor(proto, 'value');
  desc?.set?.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

/** Execute a control command inside the PM iframe (Complic Chrome). */
export async function executeIframeCommand(
  iframe: HTMLIFrameElement | null,
  cmd: IframeCommand
): Promise<{
  ok: boolean;
  error?: string;
  url?: string;
  title?: string;
  body_text?: string;
  ui_map?: IframeUiElement[];
  headings?: string[];
  nav_links?: IframeNavLink[];
}> {
  if (!iframe) return { ok: false, error: 'no iframe element' };
  let doc: Document | null = null;
  let win: Window | null = null;
  try {
    doc = iframe.contentDocument;
    win = iframe.contentWindow;
    if (!doc || !win) return { ok: false, error: 'iframe not accessible' };
    void doc.location.href;
  } catch (e: any) {
    return { ok: false, error: e?.message || 'cross-origin blocked' };
  }

  const action = (cmd.action || '').toLowerCase();
  try {
    if (action === 'goto') {
      let target = (cmd.url || '').trim();
      if (!target) return { ok: false, error: 'url required for goto' };
      if (target.startsWith('/')) {
        target = `${win.location.origin}${target}`;
      }
      win.location.href = target;
      await new Promise((r) => setTimeout(r, 1200));
    } else if (action === 'inspect') {
      // no-op mutation — just return richer snapshot below
      await new Promise((r) => setTimeout(r, 50));
    } else if (action === 'reload') {
      win.location.reload();
      await new Promise((r) => setTimeout(r, 1200));
    } else if (action === 'click') {
      let el: HTMLElement | null = null;
      if (cmd.selector) el = doc.querySelector(cmd.selector) as HTMLElement | null;
      if (!el && cmd.text) el = findByText(doc, cmd.text);
      if (!el) return { ok: false, error: `element not found (${cmd.selector || cmd.text})` };
      el.scrollIntoView({ block: 'center', inline: 'nearest' });
      el.click();
      await new Promise((r) => setTimeout(r, 400));
    } else if (action === 'type' || action === 'fill') {
      let el: HTMLElement | null = null;
      if (cmd.selector) el = doc.querySelector(cmd.selector) as HTMLElement | null;
      if (!el) {
        el = doc.querySelector('input:focus, textarea:focus') as HTMLElement | null;
      }
      if (!el) return { ok: false, error: `input not found (${cmd.selector || 'focused'})` };
      el.focus();
      const text = cmd.text ?? '';
      if (action === 'fill' || (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
        if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
          setNativeValue(el, text);
        } else {
          el.textContent = text;
        }
      } else {
        for (const ch of text) {
          el.dispatchEvent(new KeyboardEvent('keydown', { key: ch, bubbles: true }));
          el.dispatchEvent(new KeyboardEvent('keypress', { key: ch, bubbles: true }));
          el.dispatchEvent(new KeyboardEvent('keyup', { key: ch, bubbles: true }));
        }
      }
      await new Promise((r) => setTimeout(r, 200));
    } else if (action === 'press') {
      const key = cmd.key || 'Enter';
      const active = (doc.activeElement as HTMLElement) || doc.body;
      active.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true }));
      active.dispatchEvent(new KeyboardEvent('keyup', { key, bubbles: true }));
      if (key === 'Enter' && active instanceof HTMLInputElement) {
        active.form?.requestSubmit?.();
      }
      await new Promise((r) => setTimeout(r, 300));
    } else if (action === 'select') {
      if (!cmd.selector) return { ok: false, error: 'selector required for select' };
      const el = doc.querySelector(cmd.selector) as HTMLSelectElement | null;
      if (!el) return { ok: false, error: `select not found (${cmd.selector})` };
      el.value = cmd.value || cmd.text || '';
      el.dispatchEvent(new Event('change', { bubbles: true }));
      await new Promise((r) => setTimeout(r, 200));
    } else {
      return { ok: false, error: `unknown action: ${action}` };
    }

    // re-read after navigation/DOM update
    doc = iframe.contentDocument;
    win = iframe.contentWindow;
    const body = doc?.body?.innerText || '';
    const ui_map = doc ? extractUiMap(doc) : [];
    const headings = doc ? extractHeadings(doc) : [];
    const nav_links = doc ? extractNavLinks(doc) : [];
    return {
      ok: true,
      url: doc?.location?.href,
      title: doc?.title,
      body_text: body.replace(/\s+/g, ' ').trim().slice(0, 4000),
      ui_map,
      headings,
      nav_links,
    };
  } catch (e: any) {
    return { ok: false, error: e?.message || String(e) };
  }
}
